import { supabase } from '../clients/supabaseClient';
import type { Driver, DriverLocation } from '../types';

// ── Types ──────────────────────────────────────────────────────────────────────

export type DriverDeliveryStatus = {
    id: string | number;
    driver_id?: string | number | null;
    status?: string | null;
    created_at?: string | null;
    assigned_at?: string | null;
};

export type UpdateDriverPayload = {
    driver_name?: string;
    car_model?: string;
    number_plate?: string;
    store_id?: string;
};

// ── Driver CRUD ────────────────────────────────────────────────────────────────

export async function fetchDriversFromDb(): Promise<Driver[]> {
    const { data, error } = await supabase
        .from('drivers')
        .select('*')
        .is('deleted_at', null);

    if (error) throw error;
    return (data ?? []) as Driver[];
}

export async function fetchAvailableDriversFromDb(): Promise<Driver[]> {
    const { data, error } = await supabase
        .from('available_drivers')
        .select('*')
        .is('deleted_at', null);

    if (error) throw error;
    return (data ?? []) as Driver[];
}

export async function fetchDriverDeliveryStatusesFromDb(): Promise<DriverDeliveryStatus[]> {
    const { data, error } = await supabase
        .from('deliveries')
        .select('id, driver_id, status, created_at, assigned_at')
        .not('status', 'in', '("DELIVERY_COMPLETE","DELIVERY_FAILED","ORDER_CANCELLED")')
        .order('assigned_at', { ascending: false, nullsFirst: false });

    if (error) throw error;
    return (data ?? []) as DriverDeliveryStatus[];
}

export async function updateDriverInDb(
    driverId: string,
    patch: UpdateDriverPayload
): Promise<Driver | null> {
    const { data, error } = await supabase
        .from('drivers')
        .update(patch)
        .eq('id', driverId)
        .select('*');

    if (error) throw error;
    if (!data || data.length === 0) return null;
    return data[0] as Driver;
}

export async function updateDriverPhoneInDb(
    authId: string | number,
    cellphone: string
): Promise<void> {
    const { error } = await supabase
        .from('profiles')
        .update({ cellphone })
        .eq('id', authId);

    if (error) throw error;
}

export async function fetchDriverPhoneFromDb(authId: string | number): Promise<string> {
    const { data } = await supabase
        .from('profiles')
        .select('cellphone')
        .eq('id', authId)
        .single();

    return data?.cellphone ?? '';
}

export async function deleteDriverFromDb(authUuid: string): Promise<boolean> {
    const now = new Date().toISOString();
    const [resDrivers, resProfiles] = await Promise.all([
        supabase.from('drivers').update({ deleted_at: now }).eq('auth_id', authUuid).select(),
        supabase.from('profiles').update({ deleted_at: now }).eq('id', authUuid).select(),
    ]);
    return (resDrivers.data?.length ?? 0) > 0 && (resProfiles.data?.length ?? 0) > 0;
}

export async function fetchAllDriverLocationsFromDb(): Promise<DriverLocation[]> {
    const { data, error } = await supabase
        .from('latest_driver_locations')
        .select('*');

    if (error) throw error;
    return (data ?? []) as DriverLocation[];
}

export type CreateDriverPayload = {
    email: string;
    password: string;
    first_name: string;
    last_name: string;
    cellphone: string;
    car_model: string;
    number_plate: string;
    store_id: string | null;
};

export async function createDriverInDb(payload: CreateDriverPayload): Promise<void> {
    const { data: authData, error: authError } = await supabase.auth.signUp({
        email:    payload.email,
        password: payload.password,
    });

    if (authError) throw authError;
    if (!authData?.user) throw new Error('Auth signup returned no user');

    const { error: profileError } = await supabase
        .from('profiles')
        .insert({
            id:         authData.user.id,
            first_name: payload.first_name,
            last_name:  payload.last_name,
            cellphone:  payload.cellphone,
            is_admin:   false,
            role:       'driver',
        });

    if (profileError) throw profileError;

    const { error: driverError } = await supabase
        .from('drivers')
        .insert([{
            driver_name:  `${payload.first_name} ${payload.last_name}`,
            auth_id:      authData.user.id,
            car_model:    payload.car_model,
            number_plate: payload.number_plate,
            store_id:     payload.store_id ?? '',
        }]);

    if (driverError) throw driverError;
}